<template>
  <div class="rounded bg-white shadow-md">
    <div v-if="content.loading" class="px-8 pt-8">
      <content-placeholders class="w-full">
        <content-placeholders-text class="w-full mb-2" :lines="1" />
        <content-placeholders-img class="w-full mb-6" />
        <content-placeholders-text class="w-full mb-2" :lines="1" />
        <content-placeholders-img class="w-full mb-6" />
        <content-placeholders-text class="w-full mb-2" :lines="1" />
        <content-placeholders-img class="w-full mb-6" />
        <content-placeholders-text class="w-full mb-2" :lines="1" />
        <content-placeholders-img class="w-full mb-6" />
      </content-placeholders>
    </div>
    <form v-if="content.success" class="px-8 pt-8">
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-astm-method"
          >
            Test Method <small>(optional)</small>
          </label>
          <div class="relative">
            <Multiselect
              v-model="form.testMethods"
              :options="options.testMethods"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Select a Test Method"
              label="name"
              track-by="name"
            >
              <template slot="caret">
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                >
                  <svg
                    class="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                    />
                  </svg>
                </div>
              </template>
            </Multiselect>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-validity"
          >
            Validity
          </label>
          <div class="relative mb-2">
            <select
              v-model="form.validity"
              :class="{
                'border-gray-200': $v.form.validity.required,
                'border-red-500': !$v.form.validity.required
              }"
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-validity"
            >
              <option disabled :value="null">-Select-</option>
              <option
                v-for="option in options.validitiesOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
          <p
            v-if="!$v.form.validity.required"
            class="text-red-500 text-xs italic"
          >
            Please fill out this field.
          </p>
        </div>
        <div class="w-full px-3" v-if="(form.validity == validate_days_text || form.validity == validate_date_text)">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-validity-value"
          >
            <span v-if="form.validity == validate_days_text">Days</span>
            <span v-if="form.validity == validate_date_text">Date</span>
          </label>
          <div class="relative mb-2">
            <input
              v-model="form.validity_days"
              :class="{
                'border-gray-200': !$v.form.validity_days.$invalid,
                'border-red-500': $v.form.validity_days.$invalid,
                'focus:border-gray-500': !$v.form.validity_days.$invalid,
                'focus:border-red-500': $v.form.validity_days.$invalid
              }"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-2 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-validity-value"
              type="number"
              placeholder="Enter Days"
              v-if="form.validity == validate_days_text"
              min="1"
            />
            <input
              v-model="form.validity_date"
              :class="{
                'border-gray-200': !$v.form.validity_date.$invalid,
                'border-red-500': $v.form.validity_date.$invalid,
                'focus:border-gray-500': !$v.form.validity_date.$invalid,
                'focus:border-red-500': $v.form.validity_date.$invalid
              }"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-2 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-validity-value"
              type="date"
              placeholder="Enter Date"
              v-if="form.validity == validate_date_text"
              @input="limitYear"
            />
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
          <p
            v-if="!$v.form.validity_date.required"
            class="text-red-500 text-xs italic"
          >
            Please fill out this field.
          </p>
          <p
            v-if="!$v.form.validity_days.required"
            class="text-red-500 text-xs italic"
          >
            Please fill out this field.
          </p>
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-delivery"
          >
            Delivery
          </label>
          <div class="relative mb-2">
            <select
              v-model="form.delivery"
              :class="{
                'border-gray-200': $v.form.delivery.required,
                'border-red-500': !$v.form.delivery.required
              }"
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-delivery"
            >
              <option disabled :value="null">-Select-</option>
              <option
                v-for="option in options.deliveries"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
          <p
            v-if="!$v.form.delivery.required"
            class="text-red-500 text-xs italic"
          >
            Please fill out this field.
          </p>
        </div>
      </div>
      <div class="grid grid-cols-2" v-if="form.delivery === 'Custom'">
        <div class="flex flex-wrap -mx-3 mb-1 grid pr-4">
          <div class="w-full px-3">
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
              for="grid-delivery-from"
            >
              Delivery From
            </label>
            <input
              v-model="form.deliveryFrom"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-2 leading-tight focus:outline-none focus:bg-white"
              id="delivery-from"
              type="number"
              placeholder="1"
              autocomplete="off"
              min="1"
            />
            <p
              v-if="!$v.form.deliveryFrom.required"
              class="text-red-500 text-xs italic"
            >
              Please fill out this field.
            </p>
            <p
              v-if="!$v.form.deliveryFrom.minValue"
              class="text-red-500 text-xs italic"
            >
              Incorrect value
            </p>
          </div>
        </div>
        <div
          class="flex flex-wrap -mx-3 mb-1"
          v-if="form.delivery === 'Custom'"
        >
          <div class="w-full px-3">
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
              for="grid-delivery-to"
            >
              Delivery To
            </label>
            <input
              v-model="form.deliveryTo"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-2 leading-tight focus:outline-none focus:bg-white"
              id="delivery-to"
              type="number"
              placeholder="2"
              autocomplete="off"
              min="1"
            />
            <p
              v-if="!$v.form.deliveryTo.required"
              class="text-red-500 text-xs italic"
            >
              Please fill out this field.
            </p>
            <p
              v-if="!$v.form.deliveryTo.minValue"
              class="text-red-500 text-xs italic"
            >
              the Delivery To must be greater than the Delivery From
            </p>
          </div>
        </div>
        <div
          class="flex flex-wrap mb-4 col-span-2"
          v-if="form.delivery === 'Custom'"
        >
          <span
            class="block uppercase tracking-wide text-gray-700 text-xs mb-2"
            for="grid-delivery-to"
          >
            {{ this.deliveryBetweenText() }}
          </span>
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-terms"
          >
            Terms
          </label>
          <div class="relative mb-2">
            <select
              v-model="form.terms"
              :class="{
                'border-gray-200': $v.form.terms.required,
                'border-red-500': !$v.form.terms.required
              }"
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-terms"
            >
              <option disabled :value="null">-Select-</option>
              <option
                v-for="option in options.terms"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
          <p v-if="!$v.form.terms.required" class="text-red-500 text-xs italic">
            Please fill out this field.
          </p>
        </div>
        <div class="w-full px-3" v-if="(form.terms == 'Other')">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-terms-value"
          >
            Enter Others Terms
          </label>
          <div class="relative mb-2">
            <input
              v-model="form.terms_other"
              :class="{
                'border-gray-200': !$v.form.terms_other.$invalid,
                'border-red-500': $v.form.terms_other.$invalid,
                'focus:border-gray-500': !$v.form.terms_other.$invalid,
                'focus:border-red-500': $v.form.terms_other.$invalid
              }"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-2 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-tmers-value"
              placeholder="Enter Terms"
            />
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
          <p
            v-if="!$v.form.terms_other.required"
            class="text-red-500 text-xs italic"
          >
            Please fill out this field.
          </p>
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            for="grid-terms"
          >
            Price Structure
          </label>
          <div class="relative mb-2">
            <select
              v-model="form.price_structure"
              :class="{
                'border-gray-200': $v.form.price_structure.required,
                'border-red-500': !$v.form.price_structure.required
              }"
              class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              id="grid-price_structure"
            >
              <option disabled :value="null">-Select-</option>
              <option
                v-for="option in price_structure"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <svg
                class="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                />
              </svg>
            </div>
          </div>
          <p v-if="!$v.form.price_structure.required" class="text-red-500 text-xs italic">
            Please fill out this field.
          </p>
        </div>
      </div>
    </form>
    <div v-if="content.loading" class="grid grid-cols-3 gap-4 px-8 pb-8">
      <content-placeholders class="col-span-1 flex items-center justify-start">
        <content-placeholders-img class="w-full px-8 py-4" />
      </content-placeholders>
      <content-placeholders class="col-span-1 flex items-center justify-center">
        <content-placeholders-img class="w-full px-8 py-4" />
      </content-placeholders>
      <content-placeholders class="col-span-1 flex items-center justify-end">
        <content-placeholders-img class="w-full px-8 py-4" />
      </content-placeholders>
    </div>
    <div v-if="content.success" class="grid grid-cols-3 gap-4 px-8 pb-8">
      <div class="col-span-1 flex items-center justify-start">
        <button
          @click.prevent="back()"
          :disabled="ui.loading"
          class="py-4 px-8 rounded transition-all duration-500 ease-in-out transform hover:scale-95"
        >
          <span class="font-semibold text-gray-500">Back</span>
        </button>
      </div>
      <div class="col-span-1 flex items-center justify-center">
        <button
          @click.prevent="save()"
          :disabled="ui.loading"
          class="px-8 py-4 transition-all duration-500 ease-in-out transform hover:scale-95"
        >
          <template v-if="!actions.save.ui.loading">
            <p class="text-primary font-semibold leading-none">
              Save and close
            </p>
          </template>
          <template v-else>
            <LoadingSpinner
              class="fill-current text-primary text-opacity-75 inline-block h-6 w-6"
            />
          </template>
        </button>
      </div>
      <div class="col-span-1 flex items-center justify-end">
        <button
          @click.prevent="submit()"
          :disabled="actions.next.ui.loading"
          :class="{
            'bg-warmgrey-400': actions.next.ui.loading
          }"
          class="bg-primary px-8 py-4 rounded transition-all duration-500 ease-in-out transform hover:scale-95"
        >
          <template v-if="!actions.next.ui.loading">
            <p class="text-white font-semibold leading-none">Next</p>
          </template>
          <template v-else>
            <LoadingSpinner
              class="fill-current text-white text-opacity-75 h-6 w-6"
            />
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { updateQuote, fetchTestMethods, discountOptions } from "~/api";
import {
  required,
  requiredIf,
  numeric,
  minValue
} from "vuelidate/lib/validators";

export default {
  name: "TermsForm",

  data() {
    return {
      validate_days_text: "Valid for _ Days from Date On Quote",
      validate_date_text: "Valid until _",

      content: {
        loading: false,
        success: false,
        error: null
      },

      ui: {
        loading: false,
        success: false,
        error: null
      },

      actions: {
        back: {
          ui: {
            loading: false
          }
        },
        save: {
          ui: {
            loading: false
          }
        },
        next: {
          ui: {
            loading: false
          }
        }
      },

      price_structure: [],

      options: {
        testMethods: [],
        validities: [
          {
            value: "15 days",
            label: "15 days"
          },
          {
            value: "30 days",
            label: "30 days"
          },
          {
            value: "60 days",
            label: "60 days"
          },
          {
            value: "90 days",
            label: "90 days"
          },
          {
            value: "Until December 31st",
            label: "Until December 31st"
          }
        ],
        validitiesOptions: [
          {
            value: "Valid for _ Days from Date On Quote",
            label: "Valid for _ Days from Date On Quote"
          },
          {
            value: "Valid until _",
            label: "Valid until _"
          },
        ],
        deliveries: [
          {
            value: "In Stock",
            label: "In Stock"
          },
          {
            value: "1 to 2 weeks after receipt of order",
            label: "1 to 2 weeks after receipt of order"
          },
          {
            value: "2 to 3 weeks after receipt of order",
            label: "2 to 3 weeks after receipt of order"
          },
          {
            value: "4 to 6 weeks after receipt of order",
            label: "4 to 6 weeks after receipt of order"
          },
          {
            value: "6 to 8 weeks after receipt of order",
            label: "6 to 8 weeks after receipt of order"
          },
          {
            value: "8 to 10 weeks after receipt of order",
            label: "8 to 10 weeks after receipt of order"
          },
          {
            value: "10 to 12 weeks after receipt of order",
            label: "10 to 12 weeks after receipt of order"
          },
          {
            value: "12 to 16 weeks after receipt of order",
            label: "12 to 16 weeks after receipt of order"
          },
          {
            value: "Custom",
            label: "Custom"
          }
          /*
          {
            value: "1 to 2 weeks after receipt of order",
            label: "1 to 2 weeks after receipt of order"
          },
          {
            value: "2 to 4 weeks after receipt of order",
            label: "2 to 4 weeks after receipt of order"
          },
          {
            value: "4 to 6 weeks after receipt of order",
            label: "4 to 6 weeks after receipt of order"
          },
          {
            value: "6 to 9 weeks after receipt of order",
            label: "6 to 9 weeks after receipt of order"
          }
          */
        ],
        terms: [
          {
            value: "Net 30 Days",
            label: "Net 30 Days"
          },
          {
            value: "Payment Prior to shipment",
            label: "Payment Prior to shipment"
          },
          {
            value: "50% Prior to shipment, 50% Net 30",
            label: "50% Prior to shipment, 50% Net 30"
          },
          {
            value: "Cash Against Documents Thru Bank",
            label: "Cash Against Documents Thru Bank"
          },
          {
            value: "Credit Card",
            label: "Credit Card"
          },
          {
            value:
              "Irrevocable L/C, Confirmed by a USA Bank, Payable @ 60 Days",
            label: "Irrevocable L/C, Confirmed by a USA Bank, Payable @ 60 Days"
          },
          /* {
            value:
              "Irrevoicable L/C, confirmed by a USA bank, Payable @ 90 Days",
            label:
              "Irrevoicable L/C, confirmed by a USA bank, Payable @ 90 Days"
          }, */
          {
            value: "Irrevocable Letter of Credit, Payable at Sight",
            label: "Irrevocable Letter of Credit, Payable at Sight"
          },
          {
            value:
              "Irrevocable Letter of Credit, Confirmed by a USA bank, Payable at Sight",
            label:
              "Irrevocable Letter of Credit, Confirmed by a USA bank, Payable at Sight"
          },
          {
            value: "30% with Order, Balance Prior to Shipment",
            label: "30% with Order, Balance Prior to Shipment"
          },
          {
            value: "50% with Order, Balance Prior to Shipment",
            label: "50% with Order, Balance Prior to Shipment"
          },
          {
            value: "Payment in Full with Order",
            label: "Payment in Full with Order"
          },
          {
            value: "CAD Pyable @ 30 Days",
            label: "CAD Pyable @ 30 Days"
          },
          {
            value: "Cash On Delivery",
            label: "Cash On Delivery"
          },
          {
            value: "Due On Receipt",
            label: "Due On Receipt"
          },
          {
            value: "Net 15 Days",
            label: "Net 15 Days"
          },
          {
            value: "Net 45 Days",
            label: "Net 45 Days"
          },
          {
            value: "Net 60 Days",
            label: "Net 60 Days"
          },
          {
            value: "Net 90 Days",
            label: "Net 90 Days"
          },
          {
            value: "Other",
            label: "Other"
          }
        ],
      },

      form: {
        testMethods: [],
        validity: null,
        delivery: null,
        terms: null,
        deliveryFrom: null,
        deliveryTo: null,
        validity_date: null,
        validity_days: null,
        terms_other: null,
        price_structure: null
      }
    };
  },

  validations: {
    form: {
      validity: {
        required
      },
      delivery: {
        required
      },
      terms: {
        required
      },
      deliveryFrom: {
        required: requiredIf(function() {
          return this.form.delivery == "Custom";
        }),
        numeric,
        minValue: minValue(1)
      },
      deliveryTo: {
        required: requiredIf(function() {
          return this.form.delivery == "Custom";
        }),
        numeric,
        minValue: minValue(1)
      },
      validity_days: {
        required: requiredIf(function() {
          return this.form.validity == this.validate_days_text;
        }),
        numeric,
        minValue: minValue(1)
      },
      validity_date: {
        required: requiredIf(function() {
          return this.form.validity == this.validate_date_text;
        }),
      },
      terms_other: {
        required: requiredIf(function() {
          return this.form.terms == 'Other';
        }),
      },
      price_structure: {
        required
      }
    }
  },

  computed: {
    ...mapGetters({
      quoteId: "quoteForm/id",
      quoteRecord: "quoteForm/record"
    })
  },

  mounted() {
    this.init();
  },

  watch: {
    'form.validity': {
      handler(newValue, oldValue) {
        console.log('form.validity', newValue, oldValue);

        if (newValue == 'Valid until _') {
          if (!this.form.validity_date) {
            let date = new Date();
            let year = date.getFullYear();
            this.form.validity_date = `${year}-12-31`;
          }
        }
      },
      deep: true
    }
  },

  methods: {
    async init() {
      this.content.loading = true;
      this.content.success = false;
      this.content.error = null;

      console.log('this.quoteRecord.type', this.quoteRecord.type);

      const getDiscountOptions = await discountOptions();
      console.log('getDiscountOptions', getDiscountOptions);
      this.price_structure = getDiscountOptions.data/* .filter(item => {
        return item.quote_type.toLowerCase() == this.quoteRecord.type.toLowerCase()
      }) */.map(item => {
        return {
          value: item.id,
          label: item.name
        }
      })
      console.log('this.price_structure', this.price_structure)

      fetchTestMethods()
        .then(response => {
          console.log('response', response.length);
          this.options.testMethods = _.filter(response, testMethod => {
            return testMethod.type == this.quoteRecord.type;
          });
          console.log('this.options.testMethods', this.options.testMethods.length);

          this.set();

          setTimeout(() => {
            this.content.success = true;
          }, 3000);
        })
        .catch(error => {
          this.content.error = error;
        })
        .finally(() => {
          setTimeout(() => {
            this.content.loading = false;
          }, 3000);
        });
    },

    set() {
      this.form.testMethods = this.quoteRecord.test_methods;
      // this.form.validity = this.quoteRecord.validity
      //   ? this.quoteRecord.validity
      //   : "90 days";

      if (this.quoteRecord.validity == this.validate_days_text || this.quoteRecord.validity == this.validate_date_text) {
        this.form.validity = this.quoteRecord.validity;

        if (this.quoteRecord.validity == this.validate_days_text) {
          this.form.validity_days = this.quoteRecord.validity_value;
        } else {
          this.form.validity_date = this.quoteRecord.validity_value;
        }

      } else {
        this.form.validity = 'Valid for _ Days from Date On Quote';
        this.form.validity_days = 90;
      }

      let findDelivery = this.options.deliveries.find(
        d => d.value === this.quoteRecord.delivery
      );
      this.form.delivery =
        this.quoteRecord.delivery !== null && !findDelivery
          ? "Custom"
          : this.quoteRecord.delivery;
      this.form.terms = this.quoteRecord.terms;
      this.form.terms_other = this.quoteRecord.terms_other;

      if (this.quoteRecord.delivery !== null && !findDelivery) {
        let splitDelivery = this.quoteRecord.delivery
          .replace("weeks after receipt of order", "")
          .split("to");
        this.form.deliveryFrom = parseInt(splitDelivery[0]);
        this.form.deliveryTo = parseInt(splitDelivery[1]);
      }

      if (this.quoteRecord.price_structure) {
        this.form.price_structure = this.quoteRecord.price_structure;
      } else {
        if (this.quoteRecord.type == 'Domestic') {
          this.form.price_structure = 1;
        } else {
          this.form.price_structure = 6;
        }
      }
    },

    async submit() {
      try {
        this.actions.next.ui.loading = true;
        this.ui.loading = true;
        this.ui.success = false;
        this.ui.error = null;

        await this.validate();
        await this.updateQuote();
        await this.next();

        setTimeout(() => {
          this.ui.success = true;
        }, 3000);
      } catch (e) {
        console.error(e);
        this.ui.error = e;
      } finally {
        setTimeout(() => {
          this.ui.loading = false;
          this.actions.next.ui.loading = false;
        }, 3000);
      }
    },

    validate() {
      return new Promise((resolve, reject) => {
        if (!this.isValidForm("form")) {
          reject("Missing fields.");
          return;
        }
        resolve("All fields have been validated in Quote Terms.");
      });
    },

    updateQuote() {
      return new Promise((resolve, reject) => {
        updateQuote(this.quoteId, {
          test_method_ids: window._.map(this.form.testMethods, "id"),
          validity: this.form.validity,
          delivery:
            this.form.delivery === "Custom"
              ? `${this.form.deliveryFrom} to ${this.form.deliveryTo} weeks after receipt of order`
              : this.form.delivery,
          terms: this.form.terms,
          terms_other: this.form.terms == 'Other' ? this.form.terms_other : null,
          validity_value: this.form.validity == this.validate_days_text ? this.form.validity_days : (this.form.validity == this.validate_date_text ? this.form.validity_date : null),
          price_structure: this.form.price_structure
        })
          .then(response => {
            if (response.id) {
              resolve();
            } else {
              throw new Error("There was a problem trying to save changes.");
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    back() {
      this.$router.push({
        name: "quote.customer",
        params: { id: this.quoteId }
      });
    },

    async save() {
      try {
        this.actions.save.ui.loading = true;
        this.ui.loading = true;
        this.ui.success = false;
        this.ui.error = null;

        await this.validate();
        await this.updateQuote();
        await this.close();

        setTimeout(() => {
          this.ui.success = true;
        }, 3000);
      } catch (e) {
        this.ui.error = e;
      } finally {
        this.ui.loading = false;
        this.actions.save.ui.loading = false;
      }
    },

    close() {
      window.Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Your progress has been saved.",
        text: "Now it's safe to close this window.",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false
      });
    },

    next() {
      setTimeout(() => {
        this.$router.push({
          name: "quote.products",
          params: { id: this.quoteId }
        });
      }, 1500);
    },

    deliveryBetweenText() {
      if (
        isNaN(parseInt(this.form.deliveryFrom)) ||
        isNaN(parseInt(this.form.deliveryTo))
      ) {
        return "";
      }

      return `${this.form.deliveryFrom} to ${this.form.deliveryTo} weeks after receipt of order`;
    },

    limitYear() {
      let partes = this.form.validity_date.split('-');
      if (partes[0] && partes[0].length > 4) {
        partes[0] = partes[0].substr(0, 4);
        this.form.validity_date = partes.join('-');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.vue-content-placeholders-img {
  @apply h-12;
}
.vue-content-placeholders-img#text-area {
  @apply h-16;
}
.vue-content-placeholders-img.button {
  @apply w-56;
}
</style>
